import { DBSchema, IDBPDatabase, openDB } from 'idb';
import {
  ProjectDataNode,
  ProjectDataSnapshotCache,
  ProjectDataSnapshotCacheMetadata,
} from './project-data-repository.types';

const DATABASE_NAME = 'trimble-connect-project-data-cache-v1';
const DATABASE_VERSION = 1;
const NODES_STORE = 'nodes';
const METADATA_STORE = 'metadata';
const PROJECT_GENERATION_INDEX = 'byProjectGeneration';
const PARENT_INDEX = 'byProjectGenerationParent';

interface CachedProjectDataNode {
  readonly projectKey: string;
  readonly generation: string;
  readonly ordinal: number;
  readonly node: ProjectDataNode;
}

interface CachedProjectMetadata {
  readonly projectKey: string;
  readonly activeGeneration: string;
  readonly updatedAt: number;
}

interface ProjectDataCacheDatabase extends DBSchema {
  nodes: {
    key: [string, string, string];
    value: CachedProjectDataNode;
    indexes: {
      byProjectGeneration: [string, string];
      byProjectGenerationParent: [string, string, string];
    };
  };
  metadata: {
    key: string;
    value: CachedProjectMetadata;
  };
}

export class IndexedDbProjectDataCache implements ProjectDataSnapshotCache {
  private databasePromise: Promise<
    IDBPDatabase<ProjectDataCacheDatabase>
  > | null = null;
  private readonly nextOrdinalByGeneration = new Map<string, number>();
  private disposed = false;

  public static isSupported(): boolean {
    return typeof globalThis.indexedDB !== 'undefined';
  }

  public async getMetadata(
    projectKey: string
  ): Promise<ProjectDataSnapshotCacheMetadata> {
    const database = await this.getDatabase();
    const metadata = await database.get(METADATA_STORE, projectKey);
    return metadata
      ? {
          activeGeneration: metadata.activeGeneration,
          updatedAt: metadata.updatedAt,
        }
      : {};
  }

  public async readNode(
    projectKey: string,
    generation: string,
    nodeId: string
  ): Promise<ProjectDataNode | undefined> {
    const database = await this.getDatabase();
    const record = await database.get(NODES_STORE, [
      projectKey,
      generation,
      nodeId,
    ]);
    return record?.node;
  }

  public async readChildren(
    projectKey: string,
    generation: string,
    parentId: string
  ): Promise<readonly ProjectDataNode[]> {
    const database = await this.getDatabase();
    const records = await database.getAllFromIndex(
      NODES_STORE,
      PARENT_INDEX,
      IDBKeyRange.only([projectKey, generation, parentId])
    );
    return records
      .slice()
      .sort((left, right) => (left.ordinal ?? 0) - (right.ordinal ?? 0))
      .map((record) => record.node);
  }

  public async writePage(
    projectKey: string,
    generation: string,
    items: readonly ProjectDataNode[]
  ): Promise<void> {
    if (items.length === 0) return;

    const database = await this.getDatabase();
    const generationKey = `${projectKey}:${generation}`;
    let nextOrdinal = this.nextOrdinalByGeneration.get(generationKey) ?? 0;
    const transaction = database.transaction(NODES_STORE, 'readwrite');
    await Promise.all([
      ...items.map((node) => {
        const ordinal = nextOrdinal;
        nextOrdinal += 1;
        return transaction.store.put({
          projectKey,
          generation,
          ordinal,
          node,
        });
      }),
      transaction.done,
    ]);
    this.nextOrdinalByGeneration.set(generationKey, nextOrdinal);
  }

  public async commitGeneration(
    projectKey: string,
    generation: string
  ): Promise<void> {
    const database = await this.getDatabase();
    await database.put(METADATA_STORE, {
      projectKey,
      activeGeneration: generation,
      updatedAt: Date.now(),
    });
  }

  public async deleteGeneration(
    projectKey: string,
    generation: string
  ): Promise<void> {
    const database = await this.getDatabase();
    const transaction = database.transaction(NODES_STORE, 'readwrite');
    let cursor = await transaction.store
      .index(PROJECT_GENERATION_INDEX)
      .openCursor(IDBKeyRange.only([projectKey, generation]));

    while (cursor) {
      await cursor.delete();
      cursor = await cursor.continue();
    }
    await transaction.done;
    this.nextOrdinalByGeneration.delete(`${projectKey}:${generation}`);
  }

  public async deleteInactiveGenerations(
    projectKey: string,
    activeGeneration: string
  ): Promise<void> {
    const database = await this.getDatabase();
    const transaction = database.transaction(NODES_STORE, 'readwrite');
    const index = transaction.store.index(PROJECT_GENERATION_INDEX);
    const ranges = [
      IDBKeyRange.bound(
        [projectKey],
        [projectKey, activeGeneration],
        false,
        true
      ),
      IDBKeyRange.bound(
        [projectKey, activeGeneration],
        [projectKey, '\uffff'],
        true,
        false
      ),
    ];

    for (const range of ranges) {
      let cursor = await index.openCursor(range);
      while (cursor) {
        const generation = cursor.value.generation;
        await cursor.delete();
        this.nextOrdinalByGeneration.delete(`${projectKey}:${generation}`);
        cursor = await cursor.continue();
      }
    }
    await transaction.done;
  }

  public dispose(): void {
    this.disposed = true;
    this.nextOrdinalByGeneration.clear();
    void this.databasePromise?.then((database) => database.close());
    this.databasePromise = null;
  }

  private getDatabase(): Promise<IDBPDatabase<ProjectDataCacheDatabase>> {
    if (this.disposed) {
      return Promise.reject(
        new Error('IndexedDbProjectDataCache has been disposed')
      );
    }
    if (this.databasePromise) return this.databasePromise;
    if (!IndexedDbProjectDataCache.isSupported()) {
      return Promise.reject(
        new Error('IndexedDB is not available in this environment')
      );
    }

    this.databasePromise = openDB<ProjectDataCacheDatabase>(
      DATABASE_NAME,
      DATABASE_VERSION,
      {
        upgrade(database) {
          if (!database.objectStoreNames.contains(NODES_STORE)) {
            const nodes = database.createObjectStore(NODES_STORE, {
              keyPath: ['projectKey', 'generation', 'node.id'],
            });
            nodes.createIndex(PROJECT_GENERATION_INDEX, [
              'projectKey',
              'generation',
            ]);
            nodes.createIndex(PARENT_INDEX, [
              'projectKey',
              'generation',
              'node.parentId',
            ]);
          }
          if (!database.objectStoreNames.contains(METADATA_STORE)) {
            database.createObjectStore(METADATA_STORE, {
              keyPath: 'projectKey',
            });
          }
        },
        blocking: () => {
          void this.databasePromise?.then((database) => database.close());
          this.databasePromise = null;
        },
        terminated: () => {
          this.databasePromise = null;
        },
      }
    ).catch((error: unknown) => {
      this.databasePromise = null;
      throw error;
    });

    return this.databasePromise;
  }
}
